import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from './ProductCard';

const ProductSection = ({ title, products, showViewAll = true, sectionId }) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Map title to section ID
  const getSectionId = () => {
    if (sectionId) return sectionId;
    const titleLower = title.toLowerCase();
    if (titleLower.includes('new arrivals')) return 'new-arrivals';
    if (titleLower.includes('featured')) return 'featured';
    if (titleLower.includes('special one')) return 'special-one';
    return 'new-arrivals';
  };

  const handleViewAll = () => {
    navigate(`/products?section=${getSectionId()}`);
  };

  // Update scroll progress and button states
  const updateScrollState = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScroll = scrollWidth - clientWidth;
    const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;

    setScrollProgress(progress);
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < maxScroll - 1);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [products]);

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#8B1538]">
            {title}
          </h2>
          {showViewAll && (
            <button
              onClick={handleViewAll}
              className="text-sm sm:text-base text-[#8B1538] hover:underline flex items-center gap-1 cursor-pointer"
            >
              View all
              <span className="text-lg">→</span>
            </button>
          )}
        </div>
        
        {/* Scrollable products container */}
        <div className="relative">
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto gap-3 sm:gap-6 pb-4 scrollbar-hide"
          >
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* Scroll progress indicator and navigation buttons */}
          <div className="flex items-center justify-end gap-4 mt-4">
            {/* Scroll progress bar */}
            <div className="flex-1 max-w-[200px] h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#8B1538] transition-all duration-300 rounded-full"
                style={{ width: `${scrollProgress}%` }}
              />
            </div>

            {/* Navigation buttons */}
            <div className="flex gap-2">
              <button
                onClick={scrollLeft}
                disabled={!canScrollLeft}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 ${
                  canScrollLeft
                    ? 'border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538] hover:text-white cursor-pointer'
                    : 'border-gray-300 text-gray-300 cursor-not-allowed'
                }`}
                aria-label="Scroll left"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={scrollRight}
                disabled={!canScrollRight}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 ${
                  canScrollRight
                    ? 'border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538] hover:text-white cursor-pointer'
                    : 'border-gray-300 text-gray-300 cursor-not-allowed'
                }`}
                aria-label="Scroll right"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductSection;

